<template>
  <div>
    <div v-if="isSuccessful" id="forgotPassword">
      <SuccessIcon />
      <p class="font-bold text-base-black text-lg my-3 text-center">Reset request sent</p>
      <p class="text-grey-light-900 text-sm text-center">
        We have sent an email to {{ userStore.user.email }}. Please follow the instructions to reset your password.
      </p>
    </div>
    <div v-else id="forgotPassword">
      <div class="mb-3">
        <h3 class="text-lg font-semibold text-black">Forgot Your Password?</h3>
        <p class="text-sm text-black">Enter your mobile number and we will help you reset it.</p>
        <p v-if="errorMessage" class="text-red-700">{{ errorMessage }}</p>
      </div>

      <div v-if="isLoading">
        <LayupSpinner class="text-brand-700" small />
      </div>
      <form id="forgotPasswordForm" class="col-md-12 m-0 p-0" v-else>
        <UsernameInput
          v-model:username="username"
          v-model:username-type="usernameType"
          v-model:is-username-valid="isUsernameValid"
          :label="'Username'"
          :disabled="isLoading"
          @error="
            (error) => {
              errorMessage = error;
            }
          "
          class="cellnumber text-left"
        />
        <TurnstileWidget @success="handleSuccess" />
        <LayupButton
          @click="onSubmit"
          :disabled="isLoading || !isUsernameValid || !turnstileToken"
          :label="'Submit'"
          type="submit"
          :isLoading="isLoading"
          data-testid="forgot_password_button"
        />
      </form>
    </div>
    <div class="text-center mt-2.5">
      <p class="text-gray-700 text-sm">
        Remember your password?
        <RouterLink class="btn text-cyan-700 p-0 text-sm" to="sign-in"> Sign in </RouterLink>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupButton from '@/components/common/LayupButton.vue';
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import TurnstileWidget from '@/components/common/TurnstileWidget.vue';
import UsernameInput from '@/components/common/UsernameInput.vue';
import SuccessIcon from '@/components/icons/SuccessIcon.vue';
import { BASE_URL } from '@/config';
import { NetworkError } from '@/errors';
import { forgotPassword } from '@/services/auth';
import { useGeneralStore } from '@/stores/general';
import { useUserStore } from '@/stores/users';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ref } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  channel: string;
}>();

const router = useRouter();
const userStore = useUserStore();
const generalStore = useGeneralStore();

const username = ref('');
const usernameType = ref('EMAIL' as 'EMAIL' | 'MOBILE');
const isUsernameValid = ref(false);
const errorMessage = ref('');
const isLoading = ref(false);
const isSuccessful = ref(false);
const turnstileToken = ref('');

const onSubmit = async () => {
  try {
    isLoading.value = true;
    errorMessage.value = '';
    await forgotPassword(
      `${BASE_URL}/${props.channel}/auth/create-password`,
      username.value,
      usernameType.value,
      turnstileToken.value,
    );
    generalStore.passwordReset = true;

    addMetric(`forgot_password`, { status: 'success', channel: props.channel });
    if (usernameType.value === 'EMAIL') {
      userStore.user.email = username.value;
    } else {
      userStore.user.cellNumber = username.value;
      await router.push({ path: 'otp-verification' });
    }
    isSuccessful.value = true;
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric(`forgot_password`, { status: 'network_error', channel: props.channel });
      errorMessage.value = 'Network Error. Please try again.';
    } else {
      createException(error);
      addMetric(`forgot_password`, { status: 'error', channel: props.channel });
      if (error?.response?.data) {
        errorMessage.value = error.response.data;
      } else {
        errorMessage.value = 'Error resetting password';
      }
    }
  } finally {
    isLoading.value = false;
  }
};
function handleSuccess(token: string) {
  turnstileToken.value = token;
}
</script>
